import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { BiweeklyPeriod, PayoutFrequencyType, WeeklyAnchorStripe } from "@modules/box/boxPayoutFrequencyTypes"
import { type BoxSettingsFormFields, getBoxSettingsSchema } from "@modules/box/boxSettingsFormikType"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { useBoxSettingsActions } from "@modules/box/hooks/useBoxSettingsActions"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useEffect } from "react"

export const useBoxSettingsForm = () => {
  const { result: box } = useCurrentBox()
  const { updateBoxSettings } = useBoxSettingsActions()
  const language = useLanguage()

  const form = useAppForm({
    defaultValues:
      box?.getBoxSettingsDraft() ??
      ({
        paymentMethodPreferences: [],
        slotGeneratorInWeeks: 2,
        showSessionAttendees: true,
        payoutFrequency: {
          type: PayoutFrequencyType.MONTHLY,
          dayCalendar: 5,
          weeklyAnchorStripe: WeeklyAnchorStripe.MONDAY,
          biweeklyPeriod: BiweeklyPeriod.FIRST_AND_THIRD_WEEK,
        },
      } satisfies BoxSettingsFormFields),
    validators: {
      onSubmit: getBoxSettingsSchema(),
      onBlur: getBoxSettingsSchema(),
    },
    onSubmit: async ({ value }) => {
      console.log(value)
      if (!box) {
        toast.error(word("box.form.settings.toast.error"), {
          description: word("box.form.settings.toast.error_box_undefined"),
          classNames: errorToastClassName(),
        })
        return
      }

      try {
        await updateBoxSettings({ boxId: box.id, box: value })
        toast.success(word("box.form.settings.toast.success"), {
          classNames: successToastClassName(),
        })
      } catch (error) {
        console.error("Error updating box settings:", error)
        toast.error(word("box.form.settings.toast.error"), {
          description: error instanceof Error ? error.message : undefined,
          classNames: errorToastClassName(),
        })
      }
    },
  })

  // The Zod schema resolves its error messages through `word(...)` at validation time,
  // so already-displayed errors keep their previous-language text when the locale changes.
  // Re-validate on language change to regenerate the visible messages in the new language.
  useEffect(() => {
    if (form.state.isTouched || form.state.submissionAttempts > 0) {
      void form.validate("submit")
    }
  }, [language, form])

  return { form }
}
